import { createSelector } from 'reselect';

import { StringUtils } from '@components/UtilComponents/UtilFunctions';
import BRTABLE_CONSTANTS from './Constants';

const getData = (props) => props.data;
const getPage = (props) => props.page;
const getNumPerPage = (props) => props.numPerPage;
const getQuery = (props) => props.query;
const getSortColumnKey = (props) => props.sortConfig.columnKey;
const getSortOrder = (props) => props.sortConfig.orderBy;
const getFilterConfig = (props) => props.filterConfig;
const getFilterHandler = (props) => props.filterHandler;
const getColumnMap = (props) => props.columnMap;
const getDisplayColumns = (props) => props.displayColumns;

const getSortFunc = (type, order) => {
  if (order === '') {
    return () => 0;
  }
  switch (type) {
    case 'number':
    case 'boolean':
      return (a, b) => a - b;
    case 'string':
      return (a, b) => a.localeCompare(b);
    case 'date':
      return (a, b) => Date.parse(a) - Date.parse(b);
    default:
      return () => 0;
  }
};

const queryFilteredDataSelector = createSelector([getQuery, getData, getColumnMap], (query, data, columnMap) => {
  const trimmedQuery = query.trim();
  if (trimmedQuery.length > 0) {
    const regSearch = new RegExp(StringUtils.escapeRegExp(trimmedQuery), 'i');
    const searchableColumns = Object.entries(columnMap).filter(([, detail]) => detail.isSearchable);
    // eslint-disable-next-line no-console
    if (searchableColumns.length === 0) { console.debug('Did you forget to set isSearchable to one of the column?'); }
    return data.filter((row) => searchableColumns.some(([key]) => row[key] && regSearch.test(row[key].value)));
  }
  return data;
});

const advancedFilteredDataSelector = createSelector(
  [queryFilteredDataSelector, getFilterConfig, getFilterHandler],
  (queryFilteredData, filterConfig, filterHandler) => {
    if (filterHandler) {
      return filterHandler(queryFilteredData, filterConfig);
    }
    return queryFilteredData;
  },
);

const sortedDataSelector = createSelector(
  [advancedFilteredDataSelector, getSortColumnKey, getSortOrder, getColumnMap],
  (advancedFilteredData, sortColumnKey, orderBy, columnMap) => {
    if (advancedFilteredData.length && sortColumnKey) {
      const column = Object.entries(columnMap).find(([key]) => key === sortColumnKey);
      if (column) {
        const sortFunc = getSortFunc(column[1].type, orderBy);
        const sortedData = advancedFilteredData
          .slice(0)
          .sort((a, b) => sortFunc(a[sortColumnKey].value, b[sortColumnKey].value));
        return orderBy === BRTABLE_CONSTANTS.ASC ? sortedData : sortedData.reverse();
      }
    }
    return advancedFilteredData;
  },
);

const displayDataSelector = createSelector(
  [sortedDataSelector, getPage, getNumPerPage],
  (sortedData, page, numPerPage) => {
    const startIdx = (page - 1) * numPerPage;
    return sortedData.slice(startIdx, startIdx + numPerPage);
  },
);

const hideAnimationDataSelector = createSelector([getData, getDisplayColumns], (data, displayColumns) => {
  return data.map((row) => ({
    ...row,
    startHideAnimation: displayColumns.some((colKey) => row[colKey] && row[colKey].startHideAnimation),
  }));
});

export { displayDataSelector, advancedFilteredDataSelector, hideAnimationDataSelector };
